import { Link } from 'react-router-dom'
import SidebarToggle from '@/components/SidebarToggle'
import { PlusIcon, SearchIcon } from '@/components/Icons'
import { PROJECT_CREATE_ENABLED } from '@/constants/feature-flags'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import ProjectCard from './components/ProjectCard'
import ProjectsPagination from './components/ProjectsPagination'
import { useProjectDirectory } from './useProjectDirectory'

/**
 * The project directory: every Backlog project the reader's own grant can see,
 * with how many meetings here are attached to each.
 *
 * Read-only by design. Projects are created and renamed in Backlog, and the
 * local `Project` table has one writer that is not this app, so the screen
 * lists and links rather than offering to change anything.
 */
export default function ProjectsPage() {
  const { t } = useUiStrings()
  const directory = useProjectDirectory()
  const { state, projects, matches, visible, range } = directory

  /** The two states the reader can act on, and the only two offering the link. */
  const needsKey = state === 'connect' || state === 'reauth'
  const total = projects.length

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 sm:px-6">
        <SidebarToggle />
        <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-[var(--text-primary)]">
          {t.projectManagement}
        </h1>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-[var(--text-primary)]">{t.projectsHeading}</h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {total === 1
                ? t.projectsAccessSummaryOne
                : fmt(t.projectsAccessSummary, { count: total })}
            </p>
          </div>
          {PROJECT_CREATE_ENABLED && (
            <button
              type="button"
              className="btn-primary flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold"
            >
              <PlusIcon className="h-4 w-4" />
              {t.projectsCreate}
            </button>
          )}
        </div>

        <div className="relative mt-5">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-secondary)]" />
          <input
            type="search"
            value={directory.query}
            onChange={(event) => directory.setQuery(event.target.value)}
            placeholder={t.projectsDirectorySearch}
            aria-label={t.projectsDirectorySearch}
            disabled={state !== 'ready'}
            className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-tertiary)] pl-10 pr-3 text-sm text-[var(--text-primary)] transition-colors placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:bg-[var(--bg-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)] disabled:cursor-not-allowed disabled:opacity-60"
          />
        </div>

        {state === 'loading' && (
          <p className="mt-6 text-sm text-[var(--text-secondary)]">{t.projectLoading}</p>
        )}

        {state === 'error' && (
          <p
            role="alert"
            className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs font-medium text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
          >
            {directory.errorMessage ?? t.failedLoadProjects}
          </p>
        )}

        {needsKey && (
          <p className="mt-6 text-sm text-[var(--text-secondary)]">
            {state === 'connect' ? t.projectConnectRequired : t.projectReauthRequired}.{' '}
            {t.projectConnectHint}{' '}
            <Link
              to="/settings"
              className="font-semibold text-[var(--accent)] underline underline-offset-2"
            >
              {t.projectOpenSettings}
            </Link>
          </p>
        )}

        {state === 'ready' && total === 0 && (
          <p className="mt-6 text-sm text-[var(--text-secondary)]">{t.projectNoneHint}</p>
        )}

        {state === 'ready' && total > 0 && matches.length === 0 && (
          <p className="mt-6 text-sm text-[var(--text-secondary)]">{t.projectSearchEmpty}</p>
        )}

        {visible.length > 0 && (
          <>
            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {visible.map((project) => (
                <ProjectCard key={project.projectKey} project={project} />
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-color)] pt-4 text-xs text-[var(--text-secondary)]">
              <p className="tabular-nums">
                {fmt(t.projectsRange, {
                  start: range.start,
                  end: range.end,
                  total: matches.length,
                })}
              </p>
              <ProjectsPagination
                page={directory.page}
                pageCount={directory.pageCount}
                onPageChange={directory.setPage}
              />
            </div>
          </>
        )}
      </div>
    </>
  )
}
